import { DateTimeClockPicker, Label } from '@kalos/ui';
import { startOfDay } from 'date-fns';

import {
  countCalendarDays,
  type DateTimeRange,
  formatDayRange,
  moveRangeStart,
} from '../utils/multiDay';

type MultiDayDateRangeProps = {
  value: DateTimeRange;
  onChange: (value: DateTimeRange) => void;
};

/** Date of Service and End Date; an end before the start shows an error and can't be saved. */
export const MultiDayDateRange = ({ value, onChange }: MultiDayDateRangeProps) => {
  const endBeforeStart = value.end < value.start;
  const summary = endBeforeStart ? null : formatDayRange(value.start, value.end);
  const days = countCalendarDays(value.start, value.end);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="multi-day-start">
            Date of Service <span className="text-destructive">*</span>
          </Label>
          <DateTimeClockPicker
            id="multi-day-start"
            label="Date of Service"
            value={value.start}
            onChange={(d) => onChange(moveRangeStart(value, d))}
            step={15}
            size="compact"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="multi-day-end">
            End Date <span className="text-destructive">*</span>
          </Label>
          <DateTimeClockPicker
            id="multi-day-end"
            label="End Date"
            value={value.end}
            onChange={(d) => onChange({ ...value, end: d })}
            minDate={startOfDay(value.start)}
            step={15}
            size="compact"
            className={endBeforeStart ? 'border-destructive' : undefined}
          />
          {endBeforeStart && (
            <p role="alert" className="text-destructive text-xs font-medium">
              End Date can&apos;t be earlier than Date of Service.
            </p>
          )}
        </div>
      </div>
      {summary && (
        <div className="border-border bg-muted flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2">
          <span className="text-foreground text-sm font-medium tabular-nums">{summary}</span>
          {days > 1 && (
            <span className="text-muted-foreground text-xs">
              Shows as Day 1 of {days} on First Calls
            </span>
          )}
        </div>
      )}
    </div>
  );
};
